import React from 'react';
import Bugsnag from '@bugsnag/js';
import { Card, Column, Row, Typography } from '@vp/swan';
import { download } from '@99designs/design-services-common';
import { useAlertContext } from '@99designs/design-services-layouts';
import { __ } from '@99designs/i18n';
import { useJobQuery } from '../../graph/Job.generated';
import { FieldDisplayWrapper } from '../BriefForm/Field/FieldDisplay';
import { useWorkEntityField } from '../BriefForm/FieldGroup/useFormGroups';
import { WorkEntityField } from '../BriefForm/FormFields/WorkEntityField/WorkEntityField';
import { ReadOnlyReview } from '../BriefForm/Review/Review';
import { ViewContextProvider } from '../BriefForm/ViewContext';
import {
    BriefProductFragment,
    BriefRequestFragment,
    StructuredBriefFragment,
} from '../BriefForm/brief.generated';
import { ProductCard } from '../ProductCard/ProductCard';
import { LogomakerStartingProjectImages } from '../StartingProjectDisplay/StartingProjectImages';
import { getTitleFromDynamicFields } from '../Submit/getWorkIdFromDynamicFields';

const SHERBERT_API_BASE_URL = 'https://api.sherbert.cimpress.io/v2/assets';

const MIME_TO_EXT: Record<string, string> = {
    'image/svg+xml': 'svg',
    'image/png': 'png',
};

function getDownloadExtension(contentTypeHeader: string | null): string {
    return contentTypeHeader?.split(';')[0].trim() ?? '';
}

const ILLEGAL_FILENAME_CHARS = /[/\\:*?"<>|]/g;
const WHITESPACE = /\s+/g;

function sanitizeFilename(filename: string): string {
    return filename.trim().replace(ILLEGAL_FILENAME_CHARS, '-').replace(WHITESPACE, '_');
}

export type BriefUIBrief = StructuredBriefFragment & {
    request: BriefRequestFragment | null;
    product: BriefProductFragment | null;
};

export interface ReadOnlyBriefUIProps {
    brief: BriefUIBrief;
    printProductOptions?: Array<{ key: string; value: string }>;
    mpvId?: string | null;
    isCollaborationMode?: boolean;
    isDesigner?: boolean;
    skipJobQuery?: boolean;
    isFreelanceDesigner?: boolean;
    correlationId?: string;
    collaborationId?: string;
    condensed?: boolean;
}

export function ReadOnlyBriefUI({
    brief,
    printProductOptions,
    mpvId,
    isCollaborationMode = false,
    isDesigner = false,
    skipJobQuery = false,
    isFreelanceDesigner = false,
    correlationId,
    collaborationId,
    condensed = false,
}: ReadOnlyBriefUIProps) {
    const { showError } = useAlertContext();

    const effectiveMpvId = mpvId ?? brief.fulfilment?.mpvId;
    const productType = effectiveMpvId
        ?.replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());

    const job = useJobQuery({
        variables: { jobId: brief.id },
        skip: skipJobQuery,
    });

    const workEntity = useWorkEntityField(brief);
    const projectTitle = getTitleFromDynamicFields(brief.dynamicFields);

    const logomakerEntityId = brief.request?.optionalFacts?.logomakerEntityId;
    const logomakerEntitySig = brief.request?.optionalFacts?.logomakerEntitySig;
    const hasLogomakerEntity = !!(logomakerEntityId && logomakerEntitySig);
    const sanitizedFilename = sanitizeFilename(projectTitle || 'logomaker-design');

    return (
        <ViewContextProvider value={{ isReviewMode: true, isCollaborationMode, isDesigner }}>
            <Row>
                <Column span={12}>
                    {!condensed && (
                        <Typography fontSkin="title-section" px={'5'} mb={'7'}>
                            {__('Your design project details')}
                        </Typography>
                    )}
                    {printProductOptions && printProductOptions.length > 0 && productType && (
                        <FieldDisplayWrapper skin="standard" title={__('Selected product options')}>
                            <Typography>Product: {productType}</Typography>
                            {printProductOptions.map(({ key, value }) => (
                                <Typography key={key}>
                                    {key}: {value}
                                </Typography>
                            ))}
                        </FieldDisplayWrapper>
                    )}
                    {(brief.product || brief.request?.expertService) && (
                        <ProductCard
                            product={brief.product ?? undefined}
                            request={brief.request ?? undefined}
                            hideImage
                            isFreelanceDesigner={isFreelanceDesigner}
                            matchedDate={job.data?.job.match?.matchedAt}
                            briefId={brief.id}
                            fullWidth
                            removeBottomMargin
                        />
                    )}
                    {brief.workName && (
                        <Card bordered mt="6" p="6">
                            <Typography fontSize={'standard'} fontWeight="bold" pb={'3'}>
                                {__("The template you've started with")}
                            </Typography>
                            <Typography fontSize="small">{brief.workName}</Typography>
                            {workEntity && (
                                <WorkEntityField
                                    workEntityValue={workEntity.field.workEntityValue}
                                    title={workEntity.title}
                                    description={workEntity.description}
                                    correlationId={correlationId ?? brief.correlationId}
                                />
                            )}
                        </Card>
                    )}
                    {hasLogomakerEntity && (
                        <Card bordered mt="6" p="6">
                            <Typography fontSize={'large'} fontWeight="bold" pb={'3'}>
                                {__('Your starting project')}
                            </Typography>
                            <LogomakerStartingProjectImages
                                images={[
                                    {
                                        url: `${SHERBERT_API_BASE_URL}/${logomakerEntityId}?signature=${logomakerEntitySig}`,
                                        title: projectTitle,
                                    },
                                ]}
                                onDownload={async (image) => {
                                    const DOWNLOAD_ERROR_MESSAGE = __(
                                        'Sorry, there was an issue fetching your logomaker design for download.',
                                        {
                                            _context:
                                                'This is the error message when there is an error downloading the logomaker design from the sherbert service',
                                        }
                                    );
                                    try {
                                        const response = await fetch(image.url, {
                                            method: 'HEAD',
                                        });
                                        if (!response.ok) {
                                            throw new Error(
                                                `Failed to fetch logomaker asset for download: ${response.status} ${response.statusText}`
                                            );
                                        }
                                        const contentType = getDownloadExtension(
                                            response.headers.get('content-type')
                                        );
                                        const ext = MIME_TO_EXT[contentType];
                                        if (!ext) {
                                            throw new Error(
                                                `Unsupported content-type for logomaker asset download: ${contentType}`
                                            );
                                        }
                                        await download(image.url, `${sanitizedFilename}.${ext}`);
                                    } catch (error) {
                                        Bugsnag.notify(error as Error, (event) => {
                                            event.addMetadata('logomaker', {
                                                imageUrl: image.url,
                                                sanitizedFilename,
                                                briefId: brief.id,
                                                requestId: brief.request?.id,
                                            });
                                        });
                                        showError(DOWNLOAD_ERROR_MESSAGE);
                                    }
                                }}
                            />
                        </Card>
                    )}
                    <ReadOnlyReview brief={brief} />
                    {collaborationId && (
                        <>
                            <Typography fontSkin="footnote" textColor="subtle">
                                {__('Design Service request ID:')}
                            </Typography>
                            <Typography fontSkin="footnote-bold" textColor="subtle">
                                {collaborationId}
                            </Typography>
                            <Typography fontSkin="footnote" textColor="subtle" mt="4">
                                {__(
                                    'Share this code if you need to contact the VistaPrint Customer Care team'
                                )}
                            </Typography>
                        </>
                    )}
                </Column>
            </Row>
        </ViewContextProvider>
    );
}
